import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { Item } from '../models/item.model';
import { ModalController } from '@ionic/angular';
import { DateFilterHelpModalComponent } from '../modals/date-filter-help-modal/date-filter-help-modal.component';
import { RejectionModalComponent } from '../modals/rejection-modal/rejection-modal.component';
import { LoginModalComponent } from '../modals/login-modal/login-modal.component';
import { ForgotPasswordModalComponent } from '../modals/forgot-password-modal/forgot-password-modal.component';
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component';
import { AddModalComponent } from '../modals/add-modal/add-modal.component';
import { Country } from '../models/country.model';
import { ExpertiseArea, Language } from '../models/registration.model';
import { CountrySelectionModalComponent } from '../modals/country-selection-modal/country-selection-modal.component';
import { LanguageSelectionModalComponent } from '../modals/language-selection-modal/language-selection-modal.component';
import { ExpertiseAreasSelectionModalComponent } from '../modals/expertise-areas-selection-modal/expertise-areas-selection-modal.component';
import { availableCountries, availableExpertiseAreas, availableLanguages } from '../constants/constants';
import { UserProfile } from '../models/profile.model';
import { EditProfileModalComponent } from '../modals/edit-profile-modal/edit-profile-modal.component';
import { SessionTimeoutModalComponent } from '../modals/session-timeout-modal/session-timeout-modal.component';
import { NotificationInfoModalComponent } from '../modals/notification-info-modal/notification-info-modal.component';
import { sortFilterOptions } from '../utils/filter-option-order.util';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  constructor(private modalController: ModalController) { }

  async openAddModal(props: { currentUser: any, page: string, itemToEdit?: Item }): Promise<any> {
    const modal = await this.modalController.create({
      component: AddModalComponent,
      cssClass: 'add-modal',
      componentProps: {
        currentUser: props.currentUser,
        page: props.page,
        itemToEdit: props.itemToEdit
      }
    });
    await modal.present();
    return modal.onDidDismiss();
  }

  async openConfirmationModal(props: any): Promise<any> {
    const modal = await this.modalController.create({
      component: ConfirmationModalComponent,
      componentProps: {
        title: props.title,
        message: props.message,
        confirmButtonText: props.confirmText,
        cancelButtonText: props.cancelText,
        showDocumentUpload: props.showDocumentUpload,
        item: props.pendingItem,
        isUploading: props.isUploading,
        isProcessing: props.isProcessing,
      },
      backdropDismiss: false,
      cssClass: 'confirmation-modal',
    });
    await modal.present();
    return modal.onDidDismiss();
  }

  async openDateFilterHelpModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: DateFilterHelpModalComponent,
      cssClass: 'date-filter',
    });
    return await modal.present();
  }

  async openLoginModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: LoginModalComponent,
      cssClass: 'login-modal-size',
    });
    modal.onDidDismiss().then(async (detail) => {
      if (detail.data?.openForgotPassword) {
        await this.openForgotPasswordModal();
      }
    });
    return await modal.present();
  }

  async openForgotPasswordModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: ForgotPasswordModalComponent,
      cssClass: 'forgot-password-modal-size',
    });
    return await modal.present();
  }

  openRejectionModal(): Observable<string | undefined> {
    const createAndPresentModal = async () => {
      const modal = await this.modalController.create({
        component: RejectionModalComponent,
        cssClass: 'rejection-modal',
      });
      await modal.present();
      const { data } = await modal.onDidDismiss();
      return data;
    };
    return from(createAndPresentModal()).pipe(map(result => result?.rejectionReason));
  }

  async openCountrySelectionModal(selectedCountry: Country | null): Promise<Country | null> {
    const modal = await this.modalController.create({
      component: CountrySelectionModalComponent,
      cssClass: 'country-selection-modal',
      componentProps: {
        availableCountries: sortFilterOptions(availableCountries),
        selectedCountry: selectedCountry,
      },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    return data?.selectedCountry || null;
  }

  async openLanguageSelectionModal(selectedLanguages: Language[] | null): Promise<Language[] | null> {
    const modal = await this.modalController.create({
      component: LanguageSelectionModalComponent,
      cssClass: 'language-selection-modal',
      componentProps: {
        availableLanguages: sortFilterOptions(availableLanguages),
        selectedLanguages: selectedLanguages || [],
      },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    return data?.selectedLanguages || null;
  }

  async openExpertiseAreasModal(selectedAreas: ExpertiseArea[] | null): Promise<ExpertiseArea[] | null> {
    const modal = await this.modalController.create({
      component: ExpertiseAreasSelectionModalComponent,
      cssClass: 'expertise-areas-modal',
      componentProps: {
        availableExpertiseAreas: sortFilterOptions(availableExpertiseAreas),
        selectedExpertiseAreas: selectedAreas ? [...selectedAreas] : [],
      },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    return data?.selectedExpertiseAreas || null;
  }

  async openSessionTimeoutModal(remainingTime: string | null): Promise<HTMLIonModalElement> {
    const modal = await this.modalController.create({
      component: SessionTimeoutModalComponent,
      componentProps: {
        remainingTime: remainingTime,
      },
      backdropDismiss: false,
      cssClass: 'session-timeout-modal',
    });
    return modal;
  }

  async openEditProfileModal(props: { profile: UserProfile }): Promise<any> {
    const modal = await this.modalController.create({
      component: EditProfileModalComponent,
      cssClass: 'edit-profile',
      componentProps: {
        profile: props.profile,
      },
    });
    await modal.present();
    return await modal.onDidDismiss();
  }

  async openInfoDialog(p0?: { title: string; message: string; }) {
    const modal = await this.modalController.create({
      component: NotificationInfoModalComponent,
      cssClass: 'notification-info-modal',
    });
    await modal.present();
  }

  async dismiss(data?: any): Promise<void> {
    try {
      const top = await this.modalController.getTop();
      if (top) {
        await this.modalController.dismiss(data);
      }
    } catch (error) {
      console.error(error)
    }
  }
}
